import {AppRoute} from "./enum";
import {PermissionKey} from '../../components/Access/helper/permission/enum/PermissionKey';
import {SubscriptionScopeKey} from '../../components/Access/helper/subscription/enum/SubscriptionScopeKey';
import {flattenRouteInfo} from "../helpers/flattenRouteInfo";
import { AppName } from "../../containers/AppPage/ControlPanel/Apps/enums/AppName";

/**
 * ROUTE
 * - id: Route
 * - path: string
 * - children: ROUTE[] [optional]
 *
 * Route Blocking due to Permission or Subscription
 * - permission: PermissionKey
 * - subscription: SubscriptionScopeKey
 * - create: boolean
 * - update: boolean
 * - view: boolean
 * - inhibitorModifiers: Array of M3 / M4. If any of the modifier is present, the route will be blocked
 *
 * Wrapper Provider
 * - wrapper: Provider
 * - wrapperProps: props-like object {}
 *
 * Miscellaneous
 * - sidebarParentId: Route
 * - devOnly: boolean
 * - redirectTo: Route
 */

const AppRouteInfo = [
    {
        id: AppRoute.Root,
        path: "/",
    },
    ...(!process.env.REACT_APP_SHOW_AI || process.env.REACT_APP_SHOW_AI === "false") ? [] : [{
        id: AppRoute.BukkuAI,
        path: "bukku-ai",
        // view: true,
        // permission: PermissionKey.dashboard,
        // subscription: SubscriptionScopeKey.DASHBOARD,
    }],
    {
        id: AppRoute.Dashboard,
        path: "dashboard",
        view: true,
        permission: PermissionKey.dashboard,
        subscription: SubscriptionScopeKey.DASHBOARD,
    },
    {
        id: AppRoute.Dashboard2,
        path: "dashboard2",
        view: true,
        permission: PermissionKey.dashboard,
        subscription: SubscriptionScopeKey.DASHBOARD,
    },
    {
        path: "financing-portal",
        permission: PermissionKey.financing,
        allowedCountryCodes: ['MY'],
        children: [
            {
                id: AppRoute.Financing,
                path: "/",
                view: true,
            },
        ],
    },
    {
        path: "sales",
        permission: PermissionKey.sale,
        subscription: SubscriptionScopeKey.SALE,
        children: [
            {
                id: AppRoute.Sales,
                path: "/",
                view: true,
            },
            {
                path: "quotes",
                permission: PermissionKey.sale_quote,
                children: [
                    {
                        id: AppRoute.SalesQuotations,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.SalesQuotationsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.SalesQuotationsBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.SalesQuotationsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "orders",
                permission: PermissionKey.sale_order,
                children: [
                    {
                        id: AppRoute.SalesOrders,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.SalesOrdersNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.SalesOrdersBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.SalesOrdersView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "delivery-orders",
                permission: PermissionKey.sale_delivery_order,
                children: [
                    {
                        id: AppRoute.SalesDeliveryOrders,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.SalesDeliveryOrdersNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.SalesDeliveryOrdersBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey["TRANSACTION.BULK_CREATE"],
                    },
                    {
                        id: AppRoute.SalesDeliveryOrdersView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "invoices",
                permission: PermissionKey.sale_invoice,
                children: [
                    {
                        id: AppRoute.SalesInvoices,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.SalesInvoicesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.SalesInvoicesBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.SalesInvoicesBatchCash,
                        path: "batch-cash",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.SalesInvoicesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "credit-notes",
                permission: PermissionKey.sale_credit_note,
                children: [
                    {
                        id: AppRoute.SalesCreditNotes,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.SalesCreditNotesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.SalesCreditNotesBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.SalesCreditNotesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "payments",
                permission: PermissionKey.sale_payment,
                children: [
                    {
                        path: "/",
                        id: AppRoute.SalesPayments,
                        view: true,
                    },
                    {
                        id: AppRoute.SalesPaymentsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.SalesPaymentsModeView,
                        path: ':mode/:id/:origin?', // copy / pay
                        create: true,
                    },
                    {
                        id: AppRoute.SalesPaymentsBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.SalesPaymentsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "refunds",
                permission: PermissionKey.sale_refund,
                children: [
                    {
                        id: AppRoute.SalesRefunds,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.SalesRefundsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.SalesRefundsBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey["TRANSACTION.BULK_CREATE"],
                    },
                    {
                        id: AppRoute.SalesRefundsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                id: AppRoute.SalesOpeningInvoicesView,
                path: "opening-invoices/:id",
                view: true,
                permission: PermissionKey.sale_invoice,
                sidebarParentId: AppRoute.SalesInvoices,
            },
            {
                id: AppRoute.SalesOpeningCreditNotesView,
                path: "opening-credit-notes/:id",
                view: true,
                permission: PermissionKey.sale_credit_note,
                sidebarParentId: AppRoute.SalesCreditNotes,
            },
        ],
    },
    {
        path: "purchases",
        permission: PermissionKey.purchase,
        subscription: SubscriptionScopeKey.PURCHASE,
        children: [
            {
                id: AppRoute.Purchases,
                path: "/",
                view: true,
            },
            {
                path: "orders",
                permission: PermissionKey.purchase_order,
                children: [
                    {
                        id: AppRoute.PurchasesOrders,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.PurchasesOrdersNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.PurchasesOrdersBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.PurchasesOrdersView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "goods-received-notes",
                permission: PermissionKey.purchase_goods_received_note,
                children: [
                    {
                        id: AppRoute.PurchasesGoodsReceivedNotes,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.PurchasesGoodsReceivedNotesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.PurchasesGoodsReceivedNotesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "bills",
                permission: PermissionKey.purchase_bill,
                children: [
                    {
                        id: AppRoute.PurchasesBills,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.PurchasesBillsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.PurchasesBillsBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.PurchasesBillsBatchCash,
                        path: "batch-cash",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.PurchasesBillsBatchExpenseClaims,
                        path: "batch-expense-claims",
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.PurchasesBillsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "credit-notes",
                permission: PermissionKey.purchase_credit_note,
                children: [
                    {
                        id: AppRoute.PurchasesCreditNotes,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.PurchasesCreditNotesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.PurchasesCreditNotesBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.PurchasesCreditNotesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "payments",
                permission: PermissionKey.purchase_payment,
                children: [
                    {
                        id: AppRoute.PurchasesPayments,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.PurchasesPaymentsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.PurchasesPaymentsBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.PurchasesPaymentsModeView,
                        path: ':mode/:id/:origin?', // copy / pay
                        create: true,
                    },
                    {
                        id: AppRoute.PurchasesPaymentsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "refunds",
                permission: PermissionKey.purchase_refund,
                children: [
                    {
                        id: AppRoute.PurchasesRefunds,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.PurchasesRefundsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.PurchasesRefundsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                id: AppRoute.PurchasesOpeningBillsView,
                path: "opening-bills/:id",
                view: true,
                sidebarParentId: AppRoute.PurchasesBills,
                permission: PermissionKey.purchase_bill,
            },
            {
                id: AppRoute.PurchasesOpeningCreditNotesView,
                path: "opening-credit-notes/:id",
                view: true,
                sidebarParentId: AppRoute.PurchasesCreditNotes,
                permission: PermissionKey.purchase_bill,
            },
        ],
    },
    {
        id: AppRoute.Shoebox,
        path: "shoebox",
        permission: PermissionKey.shoebox,
        subscription: SubscriptionScopeKey['TRANSACTION.SHOEBOX'],
        view: true,
    },
    {
        path: "bank",
        permission: PermissionKey.bank,
        children: [
            {
                id: AppRoute.Bank,
                path: "/",
                view: true,
            },
            {
                path: "incomes",
                permission: PermissionKey.bank_income,
                subscription: SubscriptionScopeKey['BANK.TRANSACTION'],
                children: [
                    {
                        id: AppRoute.BankIncomes,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.BankIncomesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.BankIncomesBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.BankIncomesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "expenses",
                permission: PermissionKey.bank_expense,
                subscription: SubscriptionScopeKey['BANK.TRANSACTION'],
                children: [
                    {
                        id: AppRoute.BankExpenses,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.BankExpensesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.BankExpensesBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.BankExpensesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "transfers",
                permission: PermissionKey.bank_transfer,
                subscription: SubscriptionScopeKey['BANK.TRANSACTION'],
                children: [
                    {
                        id: AppRoute.BankTransfers,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.BankTransfersNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.BankTransfersView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "accounts",
                permission: PermissionKey.bank_account,
                children: [
                    {
                        id: AppRoute.BankAccounts,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.BankAccountsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.BankAccountsView,
                        path: ":id",
                        view: true,
                    },
                    {
                        id: AppRoute.BankAccountsReconcile,
                        path: ":accountId/reconcile",
                        view: true,
                        subscription: [
                            SubscriptionScopeKey["BANK.RECONCILIATION.MANUAL"],
                            SubscriptionScopeKey["BANK.RECONCILIATION.AUTO"],
                        ],
                        subscriptionCheckMethod: 'OR', // one of the subscription needs to be true to show the route
                    },
                ],
            },
            {
                path: "feeds",
                sidebarParentId: AppRoute.BankAccounts,
                permission: PermissionKey.bank_feed,
                children: [
                    {
                        id: AppRoute.BankFeeds,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.BankFeedTransactions,
                        path: ":bankFeedId/accounts/:accountId/transactions",
                        view: true,
                    },
                ],
            },
        ],
    },
    {
        path: "contacts",
        permission: [
            PermissionKey.contact,
            PermissionKey.contact_customer, PermissionKey.contact_supplier, PermissionKey.contact_employee,
        ],
        permissionCheckMethod: 'OR',
        subscription: SubscriptionScopeKey.CONTACT,
        children: [
            {
                id: AppRoute.Contacts,
                path: "/",
                view: true,
            },
            {
                id: AppRoute.ContactsNew,
                path: "new",
                create: true,
            },
            {
                id: AppRoute.ContactsImport,
                subscription: SubscriptionScopeKey['CONTACT.BULK_IMPORT'],
                path: "import",
                update: true,
            },
            {
                id: AppRoute.ContactsBatchUpdate,
                path: "batch-update",
                update: true,
            },
            {
                path: "groups",
                subscription: SubscriptionScopeKey['CONTACT.GROUP'],
                children: [
                    {
                        id: AppRoute.ContactsGroups,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.ContactsGroupsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.ContactsGroupsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                id: AppRoute.ContactsStatement,
                subscription: SubscriptionScopeKey['CONTACT.STATEMENT'],
                path: "statement",
                view: true,
            },
            {
                id: AppRoute.ContactsView,
                path: ":id",
                view: true,
            },
            {
                id: AppRoute.ContactsViewAuditLog,
                path: ":id/logs/:logId",
                view: true,
            },
        ],
    },
    // Products
    {
        path: 'products',
        permission: PermissionKey.product,
        subscription: SubscriptionScopeKey.PRODUCT,
        children: [
            {
                id: AppRoute.ProductsItems,
                path: '/',
                view: true,
            },
            {
                id: AppRoute.ProductsItemsNew,
                path: 'new',
                create: true,
            },
            {
                id: AppRoute.ProductsItemsImport,
                subscription: SubscriptionScopeKey['PRODUCT.BULK_IMPORT'],
                path: 'import',
                update: true,
            },
            {
                id: AppRoute.ProductsItemsBatchUpdate,
                path: 'batch-update',
                update: true,
            },
            {
                path: 'groups',
                // permission: PermissionKey.product_group,
                subscription: SubscriptionScopeKey['PRODUCT.GROUP'],
                children: [
                    {
                        id: AppRoute.ProductsGroups,
                        path: '/',
                        view: true,
                    },
                    {
                        id: AppRoute.ProductsGroupsNew,
                        path: 'new',
                        create: true,
                    },
                    {
                        id: AppRoute.ProductsGroupsView,
                        path: ':id',
                        view: true,
                    },
                ],
            },
            {
                path: 'bundles',
                permission: PermissionKey.product_bundle,
                subscription: SubscriptionScopeKey['PRODUCT.BUNDLE'],
                children: [
                    {
                        id: AppRoute.ProductsBundlesNew,
                        path: 'new',
                        create: true,
                    },
                    {
                        id: AppRoute.ProductsBundlesView,
                        path: ':id',
                        view: true,
                    },
                ],
            },
            {
                id: AppRoute.ProductsItemsView,
                path: ':id',
                view: true,
            },
            {
                id: AppRoute.ProductsViewAuditLog,
                path: ":id/logs/:logId",
                view: true,
            },
        ],
    },

    // Stock
    {
        path: 'stock',
        permission: PermissionKey.stock,
        subscription: SubscriptionScopeKey.INVENTORY,
        children: [
            {
                path: 'adjustments',
                permission: PermissionKey.stock_adjustment,
                subscription: SubscriptionScopeKey['INVENTORY.STOCK_ADJUSTMENT'],
                children: [
                    {
                        id: AppRoute.StockAdjustments,
                        path: '/',
                        view: true,
                    },
                    {
                        id: AppRoute.StockAdjustmentsNew,
                        path: 'new',
                        create: true,
                    },
                    {
                        id: AppRoute.StockAdjustmentsView,
                        path: ':id',
                        view: true,
                    },
                ],
            },
            {
                path: 'transfers',
                permission: PermissionKey.stock_transfer,
                subscription: [SubscriptionScopeKey['INVENTORY.MULTI_LOCATION'], SubscriptionScopeKey['INVENTORY.STOCK_TRANSFER']],
                subscriptionCheckMethod: 'AND', // both needs to be true to show the route
                children: [
                    {
                        id: AppRoute.StockTransfers,
                        path: '/',
                        view: true,
                    },
                    {
                        id: AppRoute.StockTransfersNew,
                        path: 'new',
                        create: true,
                        subscription: SubscriptionScopeKey['INVENTORY.MULTI_LOCATION'],
                    },
                    {
                        id: AppRoute.StockTransfersView,
                        path: ':id',
                        view: true,
                    },
                ],
            },
            {
                path: 'values',
                permission: PermissionKey.stock_value,
                children: [
                    {
                        id: AppRoute.StockValuesView,
                        path: '/',
                        view: true,
                    },
                ],
            },
        ],
    },
    {
        path: "myinvois",
        permission: PermissionKey.myinvois,
        settingsRequired: ["is_myinvois_submission_enabled"],
        allowedCountryCodes: ['MY'],
        children: [
            {
                id: AppRoute.MyInvoisDocuments,
                path: "documents",
                view: true,
                children: [
                    {
                        id: AppRoute.MyInvoisDocumentsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            ...(!process.env.REACT_APP_SHOW_MYINVOIS_INCOMING_DOCUMENTS || process.env.REACT_APP_SHOW_MYINVOIS_INCOMING_DOCUMENTS === "false") ? [] : [{
                id: AppRoute.MyInvoisIncomingDocuments,
                path: "incoming-documents",
                view: true,
            }],
            {
                id: AppRoute.MyInvoisTransactions,
                path: "transactions",
                view: true,
            },
            {
                id: AppRoute.MyInvoisBukkuMiniPos,
                path: "minipos",
                view: true,
                appsRequired: [AppName.MINIPOS],
                subscription: SubscriptionScopeKey.MINIPOS,
            },
        ],
    },
    {
        path: "reports",
        view: true,
        permission: PermissionKey.report,
        subscription: SubscriptionScopeKey.REPORT,
        children: [
            {
                id: AppRoute.Reports,
                path: "/",
                view: true,
            },
            {
                id: AppRoute.ReportsAll,
                path: "all",
                permission: PermissionKey.reports_all,
                view: true,
            },
            {
                id: AppRoute.ReportsBalanceSheet,
                path: "balance-sheet",
                view: true,
                permission: PermissionKey.report_balance_sheet,
            },
            {
                id: AppRoute.ReportsProfitLoss,
                path: "profit-loss",
                view: true,
                permission: PermissionKey.report_profit_loss,
            },
            {
                id: AppRoute.ReportsCashFlow,
                path: "cash-flow",
                view: true,
                permission: PermissionKey.report_cash_flow,
            },
            {
                id: AppRoute.ReportsTrialBalance,
                path: "trial-balance",
                view: true,
                permission: PermissionKey.report_trial_balance,
            },
            {
                id: AppRoute.ReportsAgedReceivablesSummary,
                path: "aged-receivables/summary",
                view: true,
                permission: PermissionKey.report_aged_receivable,
            },
            {
                id: AppRoute.ReportsAgedReceivablesDetail,
                path: "aged-receivables/detail",
                view: true,
                permission: PermissionKey.report_aged_receivable,
            },
            {
                id: AppRoute.ReportsAgedPayablesSummary,
                path: "aged-payables/summary",
                view: true,
                permission: PermissionKey.report_aged_payable,
            },
            {
                id: AppRoute.ReportsAgedPayablesDetail,
                path: "aged-payables/detail",
                view: true,
                permission: PermissionKey.report_aged_payable,
            },
            {
                id: AppRoute.ReportsProductSalesSummary,
                path: "product-sales/summary",
                view: true,
                permission: PermissionKey.report_product_sale,
            },
            {
                id: AppRoute.ReportsProductSalesDetail,
                path: "product-sales/detail",
                view: true,
                permission: PermissionKey.report_product_sale,
            },
            {
                id: AppRoute.ReportsProductBundleSalesSummary,
                path: "product-bundle-sales/summary",
                view: true,
                permission: PermissionKey.report_product_bundle_sale,
            },
            {
                id: AppRoute.ReportsProductPurchasesSummary,
                path: "product-purchases/summary",
                view: true,
                permission: PermissionKey.report_product_purchase,
            },
            {
                id: AppRoute.ReportsProductPurchasesDetail,
                path: "product-purchases/detail",
                view: true,
                permission: PermissionKey.report_product_purchase,
            },
            {
                id: AppRoute.ReportsItemSalesCollection,
                path: "item-sales-collection",
                view: true,
                permission: PermissionKey.report_item_sales_collection,
            },
            {
                id: AppRoute.ReportsTagSalesSummary,
                path: "tag-sales/summary",
                view: true,
                permission: PermissionKey.report_sale_by_tag,
                subscription: SubscriptionScopeKey.TAG,
            },
            {
                id: AppRoute.ReportsCustomerSalesSummary,
                path: "customer-sales/summary",
                view: true,
                permission: PermissionKey.report_sale_by_customer,
            },
            {
                id: AppRoute.ReportsGeneralLedger,
                path: "general-ledger",
                view: true,
                permission: PermissionKey.report_general_ledger,
            },
            {
                id: AppRoute.ReportsDebtorLedger,
                path: "debtor-ledger",
                view: true,
                permission: PermissionKey.report_debtor_ledger,
            },
            {
                id: AppRoute.ReportsCreditorLedger,
                path: "creditor-ledger",
                view: true,
                permission: PermissionKey.report_creditor_ledger,
            },
            {
                id: AppRoute.ReportsInvoiceSummary,
                path: "invoice-summary",
                view: true,
                permission: PermissionKey.report_invoice_summary,
            },
            {
                id: AppRoute.ReportsCreditNoteSummary,
                path: "credit-note-summary",
                view: true,
                permission: PermissionKey.report_sale_credit_note_summary,
            },
            {
                id: AppRoute.ReportsPurchaseCreditNoteSummary,
                path: "purchase-credit-note-summary",
                view: true,
                permission: PermissionKey.report_purchase_credit_note_summary,
            },
            {
                id: AppRoute.ReportsPaymentSummary,
                path: "payment-summary",
                view: true,
                permission: PermissionKey.report_sale_payment_summary,
            },
            {
                id: AppRoute.ReportsPurchasePaymentSummary,
                path: "purchase-payment-summary",
                view: true,
                permission: PermissionKey.report_purchase_payment_summary,
            },
            {
                id: AppRoute.ReportsBillSummary,
                path: "bill-summary",
                view: true,
                permission: PermissionKey.report_bill_summary,
            },
            {
                id: AppRoute.ReportsBankReconciliation,
                path: "bank-reconciliation",
                view: true,
                permission: PermissionKey.report_bank_reconciliation,
                // subscription: SubscriptionScopeKey.bank_reconciliation,
            },
            {
                id: AppRoute.ReportsReconciledTransactions,
                path: "reconciled-transactions",
                view: true,
                permission: PermissionKey.report_bank_reconciliation,
                // subscription: SubscriptionScopeKey.bank_reconciliation,
            },
            {
                id: AppRoute.ReportsInventorySummary,
                path: "inventory/summary",
                view: true,
                permission: PermissionKey.report_inventory,
                // subscription: SubscriptionScopeKey.inventory_tracking,
            },
            {
                id: AppRoute.ReportsInventoryDetail,
                path: "inventory/detail",
                view: true,
                permission: PermissionKey.report_inventory,
                // subscription: SubscriptionScopeKey.inventory_tracking,
            },
            {
                id: AppRoute.ReportsInventorySummaryByLocation,
                path: "inventory-summary-by-location",
                view: true,
                permission: PermissionKey.report_inventory,
                // subscription: SubscriptionScopeKey.inventory_tracking,
            },
            {
                id: AppRoute.ReportsProfitSummary,
                path: "profit-summary",
                view: true,
                permission: PermissionKey.report_profit_summary,
            },
            {
                id: AppRoute.ReportsProfitDetail,
                path: "profit-detail",
                view: true,
                permission: PermissionKey.report_profit_detail,
            },
            {
                id: AppRoute.ReportsCustomerQuotations,
                path: "customer-quotes",
                view: true,
                permission: PermissionKey.report_sale_quote_by_customer,
            },
            {
                id: AppRoute.ReportsSupplierOrders,
                path: "supplier-orders",
                view: true,
                permission: PermissionKey.report_purchase_order_by_supplier,
            },
            {
                id: AppRoute.ReportsSupplierPurchasesSummary,
                path: "supplier-purchases/summary",
                view: true,
                permission: PermissionKey.report_purchases_by_supplier,
            },
            {
                id: AppRoute.ReportsCustomerSalesOrders,
                path: "customer-sales-orders",
                view: true,
                permission: PermissionKey.report_sale_order_by_customer,
            },
            {
                id: AppRoute.ReportsCustomerDeliveryOrders,
                path: "customer-delivery-orders",
                view: true,
                permission: PermissionKey.report_sale_delivery_order_by_customer,
            },
            {
                id: AppRoute.ReportsSst02,
                path: "sst-02",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstSalesDetail,
                path: "sst-sales-detail",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstPurchaseDetail,
                path: "sst-purchase-detail",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstPaymentCollection,
                path: "sst-payment-collection",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstDeemedPayments,
                path: "sst-deemed-payments",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstPastPrepayments,
                path: "sst-past-prepayments",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstCurrentPrepayments,
                path: "sst-current-prepayments",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstByProducts,
                path: "sst-by-products",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsSstRecoveredPayments,
                path: "sst-recovered-payments",
                view: true,
                permission: PermissionKey.report_sst,
                subscription: SubscriptionScopeKey['TAX.SST'],
            },
            {
                id: AppRoute.ReportsFixedAssets,
                path: "fixed-assets",
                view: true,
                permission: PermissionKey.report_asset,
                subscription: SubscriptionScopeKey['ACCOUNTING.FIXED_ASSET'],
            },
            {
                id: AppRoute.ReportsDepreciationSchedule,
                path: "depreciation-schedule",
                view: true,
                permission: PermissionKey.report_asset,
                subscription: SubscriptionScopeKey['ACCOUNTING.FIXED_ASSET'],
            },
            {
                id: AppRoute.ReportsDisposalSchedule,
                path: "disposal-schedule",
                view: true,
                permission: PermissionKey.report_asset,
                subscription: SubscriptionScopeKey['ACCOUNTING.FIXED_ASSET'],
            },
            {
                id: AppRoute.ReportsFinancialSummary,
                path: "financial-summary",
                view: true,
                permission: PermissionKey.report_financial_summary,
            },
            {
                id: AppRoute.ReportsForeignExchangeSummary,
                path: "foreign-exchange-summary",
                view: true,
                permission: PermissionKey.report_exchange_gain_loss,
                // subscription: SubscriptionScopeKey.multi_currency,
            },
            // {
            //     id: AppRoute.ReportsForeignExchangeDetail,
            //     path: "foreign-exchange-detail",
            //     view: true,
            //     permission: PermissionKey.report_exchange_gain_loss,
            //     subscription: SubscriptionScopeKey.multi_currency,
            // },
            {
                id: AppRoute.ReportsUnrealisedExchangeDetail,
                path: "unrealised-exchange-detail",
                view: true,
                permission: PermissionKey.report_exchange_gain_loss,
                // subscription: SubscriptionScopeKey.multi_currency,
            },
            {
                id: AppRoute.ReportsExchangeRates,
                path: "exchange-rates",
                view: true,
                permission: PermissionKey.report_exchange_rates,
                // subscription: SubscriptionScopeKey.multi_currency,
            },
            {
                id: AppRoute.ReportsTransactionList,
                path: "transaction-list",
                view: true,
                permission: PermissionKey.report_transaction_list,
            },
            {
                id: AppRoute.ReportsAuditTrails,
                path: "audit-trails",
                view: true,
                permission: PermissionKey.report_audit_trail,
            },
            {
                id: AppRoute.ReportsPaymentVoucherSummary,
                path: "payment-voucher/summary",
                view: true,
                permission: PermissionKey.report_payment_voucher_summary,
            },
            {
                id: AppRoute.ReportsCashSalesSummary,
                path: "cash-sales/summary",
                view: true,
                permission: PermissionKey.report_cash_sales_summary,
            },
            {
                id: AppRoute.ReportsCashPurchaseSummary,
                path: "cash-purchase/summary",
                view: true,
                permission: PermissionKey.report_cash_purchase_summary,
            },
            {
                id: AppRoute.ReportsOfficialReceiptSummary,
                path: "official-receipt/summary",
                view: true,
                permission: PermissionKey.report_official_receipt_summary,
            },
            {
                id: AppRoute.ReportsJournalEntryDetail,
                path: "journal-entry-detail",
                view: true,
                permission: PermissionKey.report_journal_entry_detail,
            },
            {
                id: AppRoute.ReportsDoubleEntryDetail,
                path: "double-entry-detail",
                view: true,
                new: true,
                permission: PermissionKey.report_double_entry_detail,
            },
            {
                id: AppRoute.ReportsChartOfAccounts,
                path: "chart-of-accounts",
                view: true,
                permission: PermissionKey.report_chart_of_accounts,
                // subscription: SubscriptionScopeKey.manage_chart_of_account,
            },
        ],
    },
    {
        path: "flow",
        permission: PermissionKey.flow,
        subscription: SubscriptionScopeKey.FLOW,
        children: [
            {
                path: "streams",
                children: [
                    {
                        id: AppRoute.FlowDigests,
                        path: ":id/digests",
                        view: true,
                        children: [
                            {
                                id: AppRoute.FlowDigestsView,
                                path: ":id2",
                                view: true,
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: "accounting",
        permission: PermissionKey.accounting,
        children: [
            {
                id: AppRoute.Accounting,
                path: "/",
                view: true,
            },
            {
                path: "journal-entries",
                permission: PermissionKey.accounting_journal_entry,
                subscription: SubscriptionScopeKey['ACCOUNTING.JOURNAL_ENTRY'],
                children: [
                    {
                        id: AppRoute.AccountingJournalEntries,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingJournalEntriesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.AccountingJournalEntriesBatch,
                        path: "batch",
                        create: true,
                        subscription: SubscriptionScopeKey['TRANSACTION.BULK_CREATE'],
                    },
                    {
                        id: AppRoute.AccountingJournalEntriesPayroll,
                        path: "payroll",
                        create: true,
                        subscription: [
                            SubscriptionScopeKey['INTEGRATION.PAYROLL'],
                            SubscriptionScopeKey['ACCOUNTING.JOURNAL_ENTRY'],
                            SubscriptionScopeKey['BANK.TRANSACTION'],
                        ],
                        subscriptionCheckMethod: 'AND',
                    },
                    {
                        id: AppRoute.AccountingJournalEntriesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "contras",
                permission: PermissionKey.accounting_contra,
                subscription: SubscriptionScopeKey['ACCOUNTING.CONTRA'],
                children: [
                    {
                        id: AppRoute.AccountingContras,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingContrasNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.AccountingContrasView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "accounts",
                permission: PermissionKey.accounting_chart_of_account,
                // subscription: SubscriptionScopeKey.ACCOUNTING_ACCOUNT,
                children: [
                    {
                        id: AppRoute.AccountingAccounts,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingAccountsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.AccountingAccountsImport,
                        // subscription: SubscriptionScopeKey.ACCOUNTING_ACCOUNT_IMPORT,
                        path: "import",
                        update: true,
                    },
                    {
                        id: AppRoute.AccountingAccountsOpeningBalance,
                        path: "opening-balance",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingAccountsView,
                        path: ":id",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingAccountsViewAuditLog,
                        path: ":id/logs/:logId",
                        view: true,
                    },
                ],
            },
            {
                path: "deemed-payments",
                permission: PermissionKey.accounting_deemed_payment,
                subscription: SubscriptionScopeKey['TAX.SST'],
                // inhibitorModifiers: ["M3"],
                children: [
                    {
                        id: AppRoute.AccountingDeemedPayments,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingDeemedPaymentsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.AccountingDeemedPaymentsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "assets",
                permission: PermissionKey.accounting_fixed_asset,
                subscription: SubscriptionScopeKey['ACCOUNTING.FIXED_ASSET'],
                children: [
                    {
                        id: AppRoute.AccountingAssets,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingAssetsDepreciate,
                        path: "depreciate",
                    },
                    {
                        id: AppRoute.AccountingAssetsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.AccountingAssetsBatch,
                        path: "batch",
                        create: true,
                        inhibitorModifiers: ["M3"],
                        subscription: SubscriptionScopeKey["TRANSACTION.BULK_CREATE"],
                    },
                    {
                        id: AppRoute.AccountingAssetsView,
                        path: ":id",
                        view: true,
                    },
                    {
                        id: AppRoute.AccountingAssetsEdit,
                        path: ":id/edit",
                        update: true,
                    },
                    {
                        id: AppRoute.AccountingAssetsDispose,
                        path: ":id/dispose",
                        update: true,
                    },
                ],
            },
        ],
    },

    {
        path: "cp",
        permission: PermissionKey.control_panel,
        children: [
            {
                id: AppRoute.ControlPanel,
                path: "/",
                view: true,
            },
            {
                path: "apps",
                permission: PermissionKey.cp_app,
                allowedCountryCodes: ['MY'],
                children: [
                    {
                        id: AppRoute.Apps,
                        path: "/",
                        view: true,
                    },
                ],
            },
            {
                path: 'files',
                children: [
                    {
                        id: AppRoute.FileManager,
                        path: '/',
                        view: true,
                    },
                ],
            },
            {
                path: 'instructions',
                permission: PermissionKey.cp_instruction,
                subscription: SubscriptionScopeKey['TRANSACTION.INSTRUCTION'],
                // subscriptionCheckMethod: 'OR', // one of the subscription needs to be true to show the route
                children: [
                    {
                        id: AppRoute.Instructions,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.InstructionsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.InstructionsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "terms",
                permission: PermissionKey.cp_payment_term,
                children: [
                    {
                        id: AppRoute.Terms,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.TermsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.TermsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "currencies",
                permission: PermissionKey.cp_currency,
                children: [
                    {
                        id: AppRoute.Currencies,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.CurrenciesCode,
                        path: ":code",
                        view: true,
                        subscription: SubscriptionScopeKey.MULTI_CURRENCY,
                    },
                ],
            },
            {
                id: AppRoute.Tags,
                path: "tags",
                permission: PermissionKey.cp_tag,
                subscription: SubscriptionScopeKey.TAG,
                view: true,
            },
            {
                path: "asset-types",
                permission: PermissionKey.cp_asset_type,
                subscription: SubscriptionScopeKey['ACCOUNTING.FIXED_ASSET'],
                children: [
                    {
                        id: AppRoute.AssetTypes,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.AssetTypesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.AssetTypesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "locations",
                permission: PermissionKey.cp_location,
                // subscription: SubscriptionScopeKey.inventory_tracking,
                children: [
                    {
                        id: AppRoute.Locations,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.LocationsNew,
                        path: "new",
                        create: true,
                        subscription: [
                            SubscriptionScopeKey['INVENTORY.MULTI_LOCATION'],
                            SubscriptionScopeKey.INVENTORY,
                        ],
                    },
                    {
                        id: AppRoute.LocationsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "fields",
                permission: PermissionKey.cp_custom_field,
                subscription: SubscriptionScopeKey.CUSTOM_FIELD,
                children: [
                    {
                        id: AppRoute.Fields,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.FieldsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.FieldsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "price-levels",
                permission: PermissionKey.cp_price_level,
                subscription: SubscriptionScopeKey.PRICE_LEVEL,
                children: [
                    {
                        id: AppRoute.PriceLevels,
                        path: "/",
                        view: true,
                    },
                ],
            },
            {
                path: "payment-methods",
                permission: PermissionKey.cp_payment_method,
                children: [
                    {
                        id: AppRoute.PaymentMethods,
                        path: "/",
                        view: true,
                    },
                ],
            },
            {
                path: "form-designs",
                permission: PermissionKey.cp_form_design,
                children: [
                    {
                        id: AppRoute.FormDesigns,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.FormDesignsNew,
                        path: "new",
                        create: true,
                        subscription: SubscriptionScopeKey.MULTI_FORM_DESIGN,
                    },
                    {
                        id: AppRoute.FormDesignsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                id: AppRoute.Profile,
                path: "profile",
                permission: PermissionKey.cp_company_profile,
                view: true,
            },
            {
                id: AppRoute.Settings,
                path: "settings",
                permission: PermissionKey.cp_company_setting,
                view: true,
            },
            {
                path: "number-formats",
                permission: PermissionKey.cp_number_format,
                // subscription: SubscriptionScopeKey.MULTI_NUMBER_FORMAT,
                children: [
                    {
                        id: AppRoute.NumberFormats,
                        path: "/",
                        view: true,
                    },
                ],
            },
            {
                path: "flow",
                permission: PermissionKey.cp_flow_setting,
                subscription: SubscriptionScopeKey.FLOW,
                children: [
                    {
                        id: AppRoute.Flow,
                        path: "/",
                        view: true,
                    },
                    {
                        path: "streams",
                        children: [
                            {
                                id: AppRoute.FlowStreamSettingsView,
                                path: ":id",
                                view: true,
                                // children: [
                                //     {
                                //         id: AppRoute.FlowDigests,
                                //         path: "digests",
                                //         view: true,
                                //     },
                                // ],
                                children: [
                                    {
                                        id: AppRoute.FlowStreamMappingsView,
                                        path: "mappings",
                                        update: true,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                id: AppRoute.ShoeboxSettings,
                path: "shoebox",
                view: true,
                permission: PermissionKey.cp_shoebox_setting,
                subscription: SubscriptionScopeKey['TRANSACTION.SHOEBOX'],
            },
            {
                id: AppRoute.Subscription,
                path: "subscription",
                view: true,
                permission: PermissionKey.manage_billing,
            },
            {
                id: AppRoute.Integrations,
                path: "integrations",
                view: true,
                permission: PermissionKey.cp_integration,
            },
            {
                id: AppRoute.BackupData,
                path: "backup-data",
                permission: PermissionKey.cp_backup_data,
                view: true,
            },
        ],
    },
    {
        path: "cp/users",
        permission: PermissionKey.manage_users,
        children: [
            {
                id: AppRoute.Users,
                path: "/",
                view: true,
            },
            {
                id: AppRoute.UsersNew,
                path: "new",
                create: true,
                // subscription: SubscriptionScopeKey.multi_user,
            },
            {
                id: AppRoute.UsersView,
                path: ":id",
                view: true,
            },
        ],
    },
    {
        path: "cp-store",
        permission: PermissionKey.store,
        subscription: SubscriptionScopeKey.STORE,
        children: [
            {
                id: AppRoute.Store,
                path: "/",
                view: true,
            },
            {
                path: "orders",
                children: [
                    {
                        id: AppRoute.StoreOrders,
                        path: "/",
                        view: true,
                    },
                ],
            },
            {
                path: "categories",
                children: [
                    {
                        id: AppRoute.StoreCategories,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.StoreCategoriesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.StoreCategoriesRearrange,
                        path: "rearrange",
                    },
                    {
                        id: AppRoute.StoreCategoriesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },

            {
                path: "pages",
                children: [
                    {
                        id: AppRoute.StorePages,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.StorePagesNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.StorePagesView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                path: "coupons",
                children: [
                    {
                        id: AppRoute.StoreCoupons,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.StoreCouponsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.StoreCouponsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
            {
                id: AppRoute.StoreSettings,
                path: "settings",
                view: true,
            },
        ],
    },
    {
        path: "minipos",
        permission: PermissionKey.minipos,
        subscription: SubscriptionScopeKey.MINIPOS,
        allowedCountryCodes: ['MY'],
        appsRequired: [AppName.MINIPOS],
        children: [
            {
                id: AppRoute.MiniPos,
                path: "/",
                view: true,
            },
            {
                path: "settings",
                children: [
                    {
                        id: AppRoute.MiniPosSettings,
                        path: "/",
                        view: true,
                    },
                ],
            },
            {
                path: "orders",
                children: [
                    {
                        id: AppRoute.MiniPosOrders,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.MiniPosOrdersView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
        ],
    },
    {
        path: "partners",
        permission: PermissionKey.partners,
        children: [
            {
                id: AppRoute.Partners,
                path: "/",
                view: true,
            },
            {
                id: AppRoute.PartnersProfile,
                path: "profile",
            },
            {
                path: "clients",
                children: [
                    {
                        id: AppRoute.PartnersClients,
                        path: "/",
                    },
                    {
                        path: ":clientId/users",
                        permission: PermissionKey.manage_users,
                        children: [
                            {
                                id: AppRoute.PartnersClientsClientUsers,
                                path: "/",
                                view: true,
                            },
                            {
                                id: AppRoute.PartnersClientsClientUsersNew,
                                path: "new",
                                create: true,
                            },
                            {
                                id: AppRoute.PartnersClientsClientUsersView,
                                path: ":id",
                                view: true,
                            },
                        ],
                    },
                ],
            },
            {
                id: AppRoute.PartnersClientsReferrals,
                path: "referrals",
            },
            {
                id: AppRoute.PartnersTransactions,
                path: "transactions",
            },
            {
                id: AppRoute.PartnersEventLogs,
                path: "logs",
            },
            {
                path: "advisors",
                children: [
                    {
                        id: AppRoute.PartnersAdvisors,
                        path: "/",
                        view: true,
                    },
                    {
                        id: AppRoute.PartnersAdvisorsNew,
                        path: "new",
                        create: true,
                    },
                    {
                        id: AppRoute.PartnersAdvisorsView,
                        path: ":id",
                        view: true,
                    },
                ],
            },
        ],
    },
    {
        id: AppRoute.ImportUbs,
        path: "import/ubs",
    },
    {
        id: AppRoute.Annotate,
        path: "annotate",
    },
    {
        id: AppRoute.BukkuAdmin,
        path: "bukkuadmin",
        view: true,
        permission: PermissionKey.bukkuadmin,
    },
    {
        path: "test",
        children: [
            {
                id: AppRoute.TestDnd,
                path: "dnd",
            },
            {
                id: AppRoute.TestTodo,
                path: 'todo',
            },
            {
                id: AppRoute.TestLightbox,
                path: 'lightbox',
            },
            {
                id: AppRoute.TestLightgallery2,
                path: "lightgallery2",
            },
            {
                id: AppRoute.TestLightgallery3,
                path: "lightgallery3",
            },
            {
                id: AppRoute.TestLightgallery4,
                path: "lightgallery4",
            },
            {
                id: AppRoute.TestLightgallery5,
                path: "lightgallery5",
            },
            {
                id: AppRoute.TestLightgallery51,
                path: "lightgallery5-1",
            },
            {
                id: AppRoute.TestLightgalleryPreview,
                path: "lightgalleryPreview",
            },
            {
                id: AppRoute.TestFloatingWindow,
                path: "floating-window",
            },
            {
                id: AppRoute.BukkuZoomPanel,
                path: "zoompanel",
            },
            {
                id: AppRoute.TestPreviewInvoice,
                path: "preview-billing-invoice",
            },
        ],
    },
];

// console.log('qq flattenRouteInfo(AppRouteInfo)', flattenRouteInfo(AppRouteInfo));
export default flattenRouteInfo(AppRouteInfo);
